// Progressive enhancement for the scroll-driven hero takeover
// (see src/hero-scroll-takeover.css). Called from the landing page only (a
// useEffect in src/pages/Index.tsx) so the polyfill never downloads on
// onboarding, sign-in, or dashboard routes where it would be dead weight.
//
// `animation-timeline`/`view-timeline` are Chromium-only today, so most mobile
// (iOS Safari) traffic needs the scroll-timeline polyfill. When native support
// is present we skip it. Activation is signalled by a `scroll-timeline` class
// on <html> — the CSS gates on that rather than @supports because the polyfill
// cannot satisfy a native @supports (animation-timeline) check.
export async function enableHeroScrollTakeover(): Promise<void> {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  // Honor reduced-motion: keep the hero static and skip the polyfill download.
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

  // Idempotent: a re-mount or client-side nav back to the landing page must not
  // reload the polyfill or re-add the class.
  if (document.documentElement.classList.contains('scroll-timeline')) return;

  const supports = (decl: string) =>
    typeof CSS !== 'undefined' &&
    typeof CSS.supports === 'function' &&
    CSS.supports(decl);

  // Test the exact features the CSS relies on — a *named* view timeline, a
  // named animation timeline, and the `entry/exit` range syntax. A browser can
  // accept the anonymous `view()` value without fully supporting these, so
  // testing `animation-timeline: view()` alone would wrongly skip the polyfill
  // and leave a partially working hero. This mirrors the polyfill's own guard,
  // which keys off `animation-timeline: <custom-ident>`.
  const nativeSupport =
    supports('view-timeline-name: --figgy-hero') &&
    supports('animation-timeline: --figgy-hero') &&
    supports('animation-range: entry 0% exit 100%');

  if (!nativeSupport) {
    try {
      // Side-effect import; installs the ScrollTimeline/ViewTimeline polyfill.
      await import('scroll-timeline-polyfill/dist/scroll-timeline.js');
    } catch {
      return; // Polyfill unavailable — leave the static hero fallback in place.
    }
  }

  // Added last so the polyfill's `animationstart` hook is listening before the
  // gated hero animations begin.
  document.documentElement.classList.add('scroll-timeline');
}
